import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { Filter, FilterCategory } from '@interfaces/filters/catalog';
import { categoriesAdapter, filtersAdapter } from './filtersSlice';
import { selectSelectedMarketIds } from '@/store/slices/userSelection/selectors';
import type { CatalogLookup } from '@utils/filters/adapters';
import { isAvailableInSelectedMarkets } from '@utils/markets/availability';
import { withMarketLabels } from '@utils/filters/marketLabels';

const categoriesSelectors = categoriesAdapter.getSelectors(
  (s: RootState) => s.activeGroup.filters.categories,
);
const filtersSelectors = filtersAdapter.getSelectors(
  (s: RootState) => s.activeGroup.filters.filters,
);

export const selectAllCategories = categoriesSelectors.selectAll;
export const selectAllFilters = filtersSelectors.selectAll;
export const selectFilterById = filtersSelectors.selectById;

export const selectFilterByCode = createSelector(
  [selectAllFilters, (_: RootState, code: string) => code],
  (filters, code) => filters.find((f) => f.code === code),
);

/**
 * The whole catalog with filter and option labels resolved for the selected
 * markets (see `resolveMarketLabel`). Not narrowed: anything that renders a
 * label for a code already in the selection reads it from here.
 */
export const selectFiltersWithMarketLabels = createSelector(
  [selectAllFilters, selectSelectedMarketIds],
  (filters, marketIds): Filter[] => filters.map((f) => withMarketLabels(f, marketIds)),
);

/**
 * The single source of truth for what the filter drawer offers: filters
 * applicable in every selected market, with `options` already narrowed the
 * same way and labels resolved for those markets. OPTION-kind filters left
 * with no options are dropped entirely; RANGE-kind filters have no options
 * and gate on `filter.markets` alone.
 */
export const selectVisibleFiltersForMarkets = createSelector(
  [selectFiltersWithMarketLabels, selectSelectedMarketIds],
  (filters, marketIds): Filter[] =>
    filters
      .filter((f) => isAvailableInSelectedMarkets(f.markets, marketIds))
      .map((f) =>
        f.kind === 'RANGE'
          ? f
          : {
              ...f,
              options: f.options.filter((o) =>
                isAvailableInSelectedMarkets(o.markets, marketIds),
              ),
            },
      )
      .filter((f) => f.kind === 'RANGE' || f.options.length > 0),
);

export interface AccordionSection {
  category: FilterCategory | { id: null; name: 'Uncategorized'; displayLabel: 'Uncategorized' };
  filters: Filter[];
}

export const selectDrawerAccordion = createSelector(
  [selectAllCategories, selectVisibleFiltersForMarkets],
  (categories, filters): AccordionSection[] => {
    const map = new Map<number | null, Filter[]>();
    for (const f of filters) {
      const key = f.categoryId ?? null;
      const arr = map.get(key) ?? [];
      arr.push(f);
      map.set(key, arr);
    }
    const sections: AccordionSection[] = [];
    for (const c of categories) {
      const f = map.get(c.id);
      if (f && f.length) sections.push({ category: c, filters: f });
    }
    const uncategorized = map.get(null);
    if (uncategorized && uncategorized.length) {
      sections.push({
        category: { id: null, name: 'Uncategorized', displayLabel: 'Uncategorized' },
        filters: uncategorized,
      });
    }
    return sections;
  },
);

export const selectFilterCatalog = createSelector(
  [selectAllFilters],
  (filters): CatalogLookup => ({
    findByCode: (code: string) => filters.find((f) => f.code === code),
  }),
);
